import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

function InlineEdit({ 
  value, 
  onSave, 
  placeholder, 
  className = 'text-2xl font-bold',
  textAlign = 'center',
  multiline = true,
  disabled = false,
  noPadding = false
}) {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('common.enterText');
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Update editedValue when value prop changes
  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setEditedValue(value);
    }
  };

  const handleSave = async () => {
    const trimmedValue = editedValue.trim();
    
    // Don't save if value is empty or unchanged
    if (!trimmedValue || trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      // Keep editing mode on error
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      // Cmd+Enter or Ctrl+Enter to save
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter' && !multiline) {
      // In single-line mode, Enter saves
      e.preventDefault();
      handleSave();
    }
  };

  // Auto-resize textarea to fit content
  const autoResizeTextarea = useCallback(() => {
    if (inputRef.current && multiline) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  }, [multiline]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      
      if (multiline) {
        autoResizeTextarea();
      }
    }
  }, [isEditing, autoResizeTextarea, multiline]);

  // Resize textarea when content changes
  useEffect(() => {
    if (isEditing && multiline) {
      autoResizeTextarea();
    }
  }, [editedValue, isEditing, autoResizeTextarea, multiline]);

  const textAlignClass = textAlign === 'center' ? 'text-center' : textAlign === 'right' ? 'text-right' : 'text-left';

  const paddingClass = noPadding ? 'px-0 py-1' : 'px-2 py-1';
  const wrapperPadding = noPadding ? '' : '';

  if (isEditing) {
    if (multiline) {
      return (
        <div className={wrapperPadding}>
          <textarea
            ref={inputRef}
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={`w-full border-2 border-gray-400 rounded-md focus:outline-none bg-transparent ${paddingClass} resize-none overflow-hidden leading-tight ${className} ${textAlignClass}`}
            placeholder={defaultPlaceholder}
            rows={1}
          />
        </div>
      );
    } else {
      return (
        <div className={wrapperPadding}>
          <input
            ref={inputRef}
            type="text"
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={`w-full border-2 border-gray-400 rounded-md focus:outline-none bg-transparent ${paddingClass} leading-tight ${className} ${textAlignClass}`}
            placeholder={defaultPlaceholder}
          />
        </div>
      );
    }
  }

  return (
    <div 
      ref={containerRef}
      className={`cursor-text border-2 border-transparent hover:border-dashed hover:border-gray-300 ${paddingClass} rounded-md whitespace-pre-wrap leading-tight ${className} ${textAlignClass} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      onClick={handleClick}
      title={disabled ? '' : 'Click to edit'}
    >
      {value || placeholder}
    </div>
  );
}

InlineEdit.propTypes = {
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  multiline: PropTypes.bool,
  disabled: PropTypes.bool,
  noPadding: PropTypes.bool,
};

export default InlineEdit;

